import type { Proxy } from '#~/agent/index.js';
import type { Device } from '@studio/models/device';
import type { UsePrompt } from '@studio/design-system/prompt';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { DeviceRegistryInterfaceItem } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isDeviceDeleted } from '#~/device/commands/teardown.js';
import { logger } from '#~/logger/index.js';
import { isReactiveSetting } from './save-behavior.js';

/**
 * Enhanced mutation handler that routes between immediate and deferred mutations
 * based on the saveBehavior field of the interface item.
 */
export class ReactiveMutationHandler {
  constructor(
    private agent: T_Maybe<Proxy>,
    private onDeferredMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>,
    private prompt?: T_Maybe<UsePrompt>,
    private deviceId?: T_Maybe<Device['id']>
  ) {}

  /**
   * Handles a mutation based on the save behavior of the interface item.
   *
   * @param mutation - The control mutation to apply
   * @param item - The device registry interface item containing saveBehavior
   * @param onSuccess - Optional callback for successful mutations
   * @param onError - Optional callback for failed mutations
   */
  async handleMutation(
    mutation: DeepPartial<Control>,
    item: DeviceRegistryInterfaceItem,
    onSuccess?: () => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    /*
     * ENG-4477: a settings control can fire (or resolve) after its device was
     * deleted — e.g. the source is removed while its image/control settings
     * are still loading. An orphaned mutation aimed at the torn-down device
     * only times out and races state sync ("A second state-sync run started
     * before the previous synchronization completed"), so drop it cleanly
     * here, before any routing. Neither callback fires: the mutation was
     * neither applied nor failed — it was cancelled.
     */
    if (this.#isDeviceGone()) {
      logger.warning(
        `Dropping settings mutation for deleted device ${String(this.deviceId)} (key ${item.key}).`
      );
      return;
    }

    try {
      if (isReactiveSetting(item)) {
        // Apply immediately for reactive settings
        await this.applyImmediately(mutation);
      } else {
        // For confirmation-required settings, optionally show modal confirmation
        await this.handleConfirmationRequired(mutation, item);
      }

      onSuccess?.();
    } catch (error) {
      console.error('___', {
        mutationError: error,
        saveBehavior: item.saveBehavior,
        key: item.key
      });
      onError?.(error as Error);
    }
  }

  /**
   * Whether the handler's device has been deleted. Checked at every apply
   * point (not just handleMutation entry) because a modal confirmation can
   * resolve long after the delete (ENG-4477).
   *
   * @returns True when the device is known to be deleted.
   */
  #isDeviceGone(): boolean {
    return this.deviceId !== undefined && this.deviceId !== null && isDeviceDeleted(this.deviceId);
  }

  /**
   * Applies a mutation immediately via agent.mutate().
   *
   * @param mutation - The control mutation to apply
   */
  private async applyImmediately(mutation: DeepPartial<Control>): Promise<void> {
    if (!this.agent) {
      throw new Error('No agent available for immediate mutation');
    }

    // ENG-4477: re-check at the apply point — deferred/modal paths can reach
    // here well after handleMutation's entry guard passed.
    if (this.#isDeviceGone()) {
      logger.warning(`Dropping settings mutation for deleted device ${String(this.deviceId)}.`);
      return;
    }

    await this.agent.mutate({ control: mutation }, {});
  }

  /**
   * Handles confirmation-required settings with optional modal confirmation.
   *
   * @param mutation - The control mutation to handle
   * @param item - The device registry interface item
   */
  private async handleConfirmationRequired(
    mutation: DeepPartial<Control>,
    item: DeviceRegistryInterfaceItem
  ): Promise<void> {
    // Check if this setting requires explicit modal confirmation
    const needsModalConfirmation = this.shouldShowModalConfirmation(item);

    if (needsModalConfirmation && this.prompt) {
      // Show modal confirmation for disruptive changes
      await this.showModalConfirmation(mutation, item);
    } else {
      // Default behavior: defer to save bar
      this.applyDeferred(mutation, item);
    }
  }

  /**
   * Determines if a setting should show modal confirmation.
   * Currently, all MODAL_SAVE settings use the save bar, but this can be extended
   * for specific disruptive settings that need immediate confirmation.
   *
   * @param item - The device registry interface item
   * @returns True if modal confirmation should be shown
   */
  private shouldShowModalConfirmation(_item: DeviceRegistryInterfaceItem): boolean {
    // For now, use save bar for all confirmation-required settings
    // This can be extended to check for specific disruptive settings
    // that need immediate modal confirmation

    // TODO TAM: implement
    return false;
  }

  /**
   * Shows modal confirmation for disruptive changes.
   *
   * @param mutation - The control mutation to confirm
   * @param item - The device registry interface item
   */
  private async showModalConfirmation(
    mutation: DeepPartial<Control>,
    item: DeviceRegistryInterfaceItem
  ): Promise<void> {
    if (!this.prompt) {
      // Fallback to deferred if no prompt available
      this.applyDeferred(mutation, item);
      return;
    }

    return new Promise((resolve) => {
      this.prompt!.confirm({
        title: 'Confirm Setting Change',
        message: `Changing "${item.label}" may require a device restart or cause temporary disruption. Do you want to continue?`,
        okay: {
          label: 'Apply Change',
          variant: 'primary'
        },
        cancel: {
          label: 'Cancel'
        },
        onConfirm: async (confirmed) => {
          if (confirmed) {
            await this.applyImmediately(mutation);
          }
          resolve();
        }
      });
    });
  }

  /**
   * Defers a mutation to the save bar system.
   *
   * @param mutation - The control mutation to defer
   * @param item - The device registry interface item
   */
  private applyDeferred(mutation: DeepPartial<Control>, _item: DeviceRegistryInterfaceItem): void {
    if (!this.onDeferredMutation) {
      console.warn('___', {
        warning: 'No deferred mutation handler available, falling back to immediate mutation',
        mutation
      });
      // Fallback to immediate if no deferred handler
      this.applyImmediately(mutation).catch(console.error);
      return;
    }

    this.onDeferredMutation(mutation);
  }
}

/**
 * Factory function to create a reactive mutation handler.
 *
 * @param agent - The agent proxy for immediate mutations
 * @param onDeferredMutation - Handler for deferred mutations (save bar)
 * @param prompt - Optional prompt hook for modal confirmations
 * @param deviceId - Device the settings belong to; enables the ENG-4477
 *   deleted-device guard that cancels orphaned mutations after a delete
 * @returns A new ReactiveMutationHandler instance
 */
export const createReactiveMutationHandler = (
  agent: T_Maybe<Proxy>,
  onDeferredMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>,
  prompt?: T_Maybe<UsePrompt>,
  deviceId?: T_Maybe<Device['id']>
): ReactiveMutationHandler => {
  return new ReactiveMutationHandler(agent, onDeferredMutation, prompt, deviceId);
};
